import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { Lock, TrendingUp } from "lucide-react";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// Historical estimates based on SIPRI Yearbooks, IISS Military Balance, FAS Nuclear Notebook

interface YearData {
  year: number;
  ballistic: number;
  drones: number;
  airDefense: number;
  airAttack: number;
}

const HISTORICAL_DATA: Record<string, YearData[]> = {
  "Russia": [
    { year: 2018, ballistic: 1100, drones: 400, airDefense: 2200, airAttack: 3200 },
    { year: 2019, ballistic: 1050, drones: 500, airDefense: 2300, airAttack: 3100 },
    { year: 2020, ballistic: 1000, drones: 700, airDefense: 2400, airAttack: 3000 },
    { year: 2021, ballistic: 980, drones: 1000, airDefense: 2500, airAttack: 2900 },
    { year: 2022, ballistic: 900, drones: 1500, airDefense: 2300, airAttack: 2700 },
    { year: 2023, ballistic: 800, drones: 4500, airDefense: 2000, airAttack: 2500 },
    { year: 2024, ballistic: 750, drones: 6000, airDefense: 1900, airAttack: 2411 },
    { year: 2025, ballistic: 877, drones: 7800, airDefense: 1800, airAttack: 2411 },
  ],
  "United States": [
    { year: 2018, ballistic: 500, drones: 600, airDefense: 2000, airAttack: 6200 },
    { year: 2019, ballistic: 500, drones: 650, airDefense: 2100, airAttack: 6300 },
    { year: 2020, ballistic: 500, drones: 700, airDefense: 2200, airAttack: 6400 },
    { year: 2021, ballistic: 500, drones: 800, airDefense: 2300, airAttack: 6500 },
    { year: 2022, ballistic: 500, drones: 1000, airDefense: 2300, airAttack: 6600 },
    { year: 2023, ballistic: 510, drones: 1500, airDefense: 2350, airAttack: 6700 },
    { year: 2024, ballistic: 520, drones: 2200, airDefense: 2400, airAttack: 6740 },
    { year: 2025, ballistic: 520, drones: 3150, airDefense: 2460, airAttack: 6740 },
  ],
  "China": [
    { year: 2018, ballistic: 800, drones: 500, airDefense: 1800, airAttack: 3200 },
    { year: 2019, ballistic: 900, drones: 800, airDefense: 2000, airAttack: 3400 },
    { year: 2020, ballistic: 1000, drones: 1200, airDefense: 2200, airAttack: 3700 },
    { year: 2021, ballistic: 1100, drones: 2000, airDefense: 2400, airAttack: 4000 },
    { year: 2022, ballistic: 1200, drones: 3000, airDefense: 2600, airAttack: 4400 },
    { year: 2023, ballistic: 1350, drones: 4500, airDefense: 2700, airAttack: 4700 },
    { year: 2024, ballistic: 1450, drones: 6000, airDefense: 2800, airAttack: 5100 },
    { year: 2025, ballistic: 1550, drones: 7200, airDefense: 2800, airAttack: 5100 },
  ],
  "Ukraine": [
    { year: 2018, ballistic: 50, drones: 20, airDefense: 600, airAttack: 300 },
    { year: 2019, ballistic: 50, drones: 30, airDefense: 600, airAttack: 280 },
    { year: 2020, ballistic: 50, drones: 50, airDefense: 550, airAttack: 260 },
    { year: 2021, ballistic: 40, drones: 100, airDefense: 500, airAttack: 250 },
    { year: 2022, ballistic: 30, drones: 2000, airDefense: 400, airAttack: 200 },
    { year: 2023, ballistic: 30, drones: 15000, airDefense: 350, airAttack: 160 },
    { year: 2024, ballistic: 30, drones: 28000, airDefense: 320, airAttack: 148 },
    { year: 2025, ballistic: 30, drones: 33000, airDefense: 320, airAttack: 148 },
  ],
  "Iran": [
    { year: 2018, ballistic: 1500, drones: 500, airDefense: 400, airAttack: 500 },
    { year: 2019, ballistic: 1800, drones: 800, airDefense: 420, airAttack: 520 },
    { year: 2020, ballistic: 2000, drones: 1500, airDefense: 450, airAttack: 550 },
    { year: 2021, ballistic: 2200, drones: 2500, airDefense: 480, airAttack: 580 },
    { year: 2022, ballistic: 2500, drones: 4000, airDefense: 500, airAttack: 600 },
    { year: 2023, ballistic: 2700, drones: 6000, airDefense: 550, airAttack: 650 },
    { year: 2024, ballistic: 2800, drones: 8000, airDefense: 600, airAttack: 686 },
    { year: 2025, ballistic: 3000, drones: 9500, airDefense: 600, airAttack: 686 },
  ],
  "Israel": [
    { year: 2018, ballistic: 100, drones: 800, airDefense: 2500, airAttack: 500 },
    { year: 2019, ballistic: 110, drones: 1000, airDefense: 2600, airAttack: 510 },
    { year: 2020, ballistic: 120, drones: 1200, airDefense: 2700, airAttack: 520 },
    { year: 2021, ballistic: 130, drones: 1500, airDefense: 2800, airAttack: 530 },
    { year: 2022, ballistic: 140, drones: 2000, airDefense: 2900, airAttack: 535 },
    { year: 2023, ballistic: 145, drones: 2500, airDefense: 2500, airAttack: 535 },
    { year: 2024, ballistic: 150, drones: 3200, airDefense: 2000, airAttack: 539 },
    { year: 2025, ballistic: 150, drones: 3600, airDefense: 2000, airAttack: 539 },
  ],
  "North Korea": [
    { year: 2018, ballistic: 400, drones: 100, airDefense: 400, airAttack: 550 },
    { year: 2019, ballistic: 420, drones: 120, airDefense: 400, airAttack: 540 },
    { year: 2020, ballistic: 450, drones: 150, airDefense: 420, airAttack: 520 },
    { year: 2021, ballistic: 480, drones: 200, airDefense: 430, airAttack: 500 },
    { year: 2022, ballistic: 520, drones: 300, airDefense: 450, airAttack: 480 },
    { year: 2023, ballistic: 580, drones: 500, airDefense: 470, airAttack: 470 },
    { year: 2024, ballistic: 620, drones: 800, airDefense: 500, airAttack: 458 },
    { year: 2025, ballistic: 650, drones: 1200, airDefense: 500, airAttack: 458 },
  ],
  "India": [
    { year: 2018, ballistic: 150, drones: 50, airDefense: 500, airAttack: 1100 },
    { year: 2019, ballistic: 170, drones: 60, airDefense: 550, airAttack: 1150 },
    { year: 2020, ballistic: 190, drones: 80, airDefense: 600, airAttack: 1200 },
    { year: 2021, ballistic: 210, drones: 100, airDefense: 700, airAttack: 1250 },
    { year: 2022, ballistic: 230, drones: 150, airDefense: 800, airAttack: 1300 },
    { year: 2023, ballistic: 240, drones: 300, airDefense: 850, airAttack: 1350 },
    { year: 2024, ballistic: 250, drones: 500, airDefense: 900, airAttack: 1400 },
    { year: 2025, ballistic: 262, drones: 700, airDefense: 900, airAttack: 1400 },
  ],
  "Turkey": [
    { year: 2018, ballistic: 30, drones: 200, airDefense: 200, airAttack: 350 },
    { year: 2019, ballistic: 35, drones: 400, airDefense: 220, airAttack: 360 },
    { year: 2020, ballistic: 40, drones: 600, airDefense: 240, airAttack: 370 },
    { year: 2021, ballistic: 40, drones: 1000, airDefense: 250, airAttack: 375 },
    { year: 2022, ballistic: 45, drones: 1500, airDefense: 270, airAttack: 380 },
    { year: 2023, ballistic: 45, drones: 2200, airDefense: 280, airAttack: 385 },
    { year: 2024, ballistic: 50, drones: 2800, airDefense: 300, airAttack: 390 },
    { year: 2025, ballistic: 50, drones: 3300, airDefense: 300, airAttack: 390 },
  ],
  "France": [
    { year: 2018, ballistic: 0, drones: 20, airDefense: 350, airAttack: 700 },
    { year: 2019, ballistic: 0, drones: 22, airDefense: 360, airAttack: 710 },
    { year: 2020, ballistic: 0, drones: 25, airDefense: 370, airAttack: 720 },
    { year: 2021, ballistic: 0, drones: 25, airDefense: 380, airAttack: 725 },
    { year: 2022, ballistic: 0, drones: 28, airDefense: 390, airAttack: 730 },
    { year: 2023, ballistic: 0, drones: 80, airDefense: 395, airAttack: 730 },
    { year: 2024, ballistic: 0, drones: 100, airDefense: 400, airAttack: 734 },
    { year: 2025, ballistic: 0, drones: 130, airDefense: 400, airAttack: 734 },
  ],
  "UK": [
    { year: 2018, ballistic: 0, drones: 15, airDefense: 300, airAttack: 450 },
    { year: 2019, ballistic: 0, drones: 15, airDefense: 290, airAttack: 440 },
    { year: 2020, ballistic: 0, drones: 18, airDefense: 280, airAttack: 420 },
    { year: 2021, ballistic: 0, drones: 18, airDefense: 270, airAttack: 410 },
    { year: 2022, ballistic: 0, drones: 20, airDefense: 260, airAttack: 400 },
    { year: 2023, ballistic: 0, drones: 50, airDefense: 250, airAttack: 390 },
    { year: 2024, ballistic: 0, drones: 60, airDefense: 250, airAttack: 387 },
    { year: 2025, ballistic: 0, drones: 80, airDefense: 250, airAttack: 387 },
  ],
  "South Korea": [
    { year: 2018, ballistic: 100, drones: 30, airDefense: 500, airAttack: 600 },
    { year: 2019, ballistic: 120, drones: 40, airDefense: 530, airAttack: 620 },
    { year: 2020, ballistic: 140, drones: 50, airDefense: 560, airAttack: 640 },
    { year: 2021, ballistic: 150, drones: 70, airDefense: 600, airAttack: 660 },
    { year: 2022, ballistic: 160, drones: 100, airDefense: 630, airAttack: 680 },
    { year: 2023, ballistic: 180, drones: 200, airDefense: 660, airAttack: 700 },
    { year: 2024, ballistic: 190, drones: 350, airDefense: 700, airAttack: 710 },
    { year: 2025, ballistic: 200, drones: 550, airDefense: 700, airAttack: 710 },
  ],
  "Pakistan": [
    { year: 2018, ballistic: 140, drones: 10, airDefense: 250, airAttack: 400 },
    { year: 2019, ballistic: 150, drones: 15, airDefense: 260, airAttack: 410 },
    { year: 2020, ballistic: 160, drones: 20, airDefense: 280, airAttack: 420 },
    { year: 2021, ballistic: 170, drones: 30, airDefense: 300, airAttack: 440 },
    { year: 2022, ballistic: 180, drones: 40, airDefense: 320, airAttack: 450 },
    { year: 2023, ballistic: 190, drones: 100, airDefense: 330, airAttack: 460 },
    { year: 2024, ballistic: 195, drones: 200, airDefense: 350, airAttack: 470 },
    { year: 2025, ballistic: 200, drones: 260, airDefense: 350, airAttack: 470 },
  ],
  "Japan": [
    { year: 2018, ballistic: 0, drones: 5, airDefense: 350, airAttack: 600 },
    { year: 2019, ballistic: 0, drones: 5, airDefense: 370, airAttack: 620 },
    { year: 2020, ballistic: 0, drones: 8, airDefense: 390, airAttack: 640 },
    { year: 2021, ballistic: 0, drones: 10, airDefense: 410, airAttack: 660 },
    { year: 2022, ballistic: 0, drones: 12, airDefense: 430, airAttack: 700 },
    { year: 2023, ballistic: 0, drones: 30, airDefense: 460, airAttack: 750 },
    { year: 2024, ballistic: 0, drones: 50, airDefense: 500, airAttack: 790 },
    { year: 2025, ballistic: 0, drones: 70, airDefense: 500, airAttack: 790 },
  ],
  "Saudi Arabia": [
    { year: 2018, ballistic: 100, drones: 50, airDefense: 700, airAttack: 400 },
    { year: 2019, ballistic: 100, drones: 80, airDefense: 680, airAttack: 410 },
    { year: 2020, ballistic: 110, drones: 120, airDefense: 650, airAttack: 420 },
    { year: 2021, ballistic: 110, drones: 200, airDefense: 700, airAttack: 430 },
    { year: 2022, ballistic: 115, drones: 350, airDefense: 750, airAttack: 440 },
    { year: 2023, ballistic: 115, drones: 500, airDefense: 770, airAttack: 450 },
    { year: 2024, ballistic: 120, drones: 700, airDefense: 800, airAttack: 470 },
    { year: 2025, ballistic: 120, drones: 900, airDefense: 800, airAttack: 470 },
  ],
};

// Simple linear regression for forecasting
function linearForecast(values: number[], forecastYears: number): number[] {
  const n = values.length;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((s, v) => s + v, 0) / n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (values[i] - yMean);
    den += (i - xMean) * (i - xMean);
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = yMean - slope * xMean;
  const results: number[] = [];
  for (let j = 1; j <= forecastYears; j++) {
    results.push(Math.max(0, Math.round(intercept + slope * (n - 1 + j))));
  }
  return results;
}

interface ArmsHistoryChartProps {
  selectedCountry: string;
}

const COLORS = {
  ballistic: "hsl(0, 80%, 55%)",
  drones: "hsl(35, 90%, 55%)",
  airDefense: "hsl(200, 80%, 55%)",
  airAttack: "hsl(260, 70%, 60%)",
};

const ArmsHistoryChart = ({ selectedCountry }: ArmsHistoryChartProps) => {
  const { isPremium } = useAuth();
  const data = HISTORICAL_DATA[selectedCountry];

  const { chartData, forecasts } = useMemo(() => {
    if (!data) return { chartData: [], forecasts: [] as YearData[] };

    // Generate forecasts for 2026-2028
    const fBallistic = linearForecast(data.map(d => d.ballistic), 3);
    const fDrones = linearForecast(data.map(d => d.drones), 3);
    const fAirDef = linearForecast(data.map(d => d.airDefense), 3);
    const fAirAtk = linearForecast(data.map(d => d.airAttack), 3);

    const forecastData: YearData[] = [2026, 2027, 2028].map((yr, i) => ({
      year: yr,
      ballistic: fBallistic[i],
      drones: fDrones[i],
      airDefense: fAirDef[i],
      airAttack: fAirAtk[i],
    }));

    const allData = [...data, ...forecastData];
    const visibleData = isPremium ? allData : allData.slice(-6);

    // Confidence interval: ±15% widening each year
    const mapped = visibleData.map((d) => {
      const isForecast = d.year > 2025;
      const yearOffset = d.year - 2025; // 1, 2, 3 for forecast years
      const ciMargin = isForecast ? 0.10 + 0.05 * yearOffset : 0; // 15%, 20%, 25%
      return {
        year: d.year.toString(),
        // Historical (solid areas)
        "Ballistic Missiles": isForecast ? undefined : d.ballistic,
        "Drones & UAVs": isForecast ? undefined : d.drones,
        "Air Defense": isForecast ? undefined : d.airDefense,
        "Air Attack": isForecast ? undefined : d.airAttack,
        // Forecast (dashed lines)
        "Ballistic (forecast)": isForecast || d.year === 2025 ? d.ballistic : undefined,
        "Drones (forecast)": isForecast || d.year === 2025 ? d.drones : undefined,
        "Air Def (forecast)": isForecast || d.year === 2025 ? d.airDefense : undefined,
        "Air Atk (forecast)": isForecast || d.year === 2025 ? d.airAttack : undefined,
        // Confidence bands [low, high]
        "Ballistic CI": isForecast ? [Math.round(d.ballistic * (1 - ciMargin)), Math.round(d.ballistic * (1 + ciMargin))] : undefined,
        "Drones CI": isForecast ? [Math.round(d.drones * (1 - ciMargin)), Math.round(d.drones * (1 + ciMargin))] : undefined,
        "Air Def CI": isForecast ? [Math.round(d.airDefense * (1 - ciMargin)), Math.round(d.airDefense * (1 + ciMargin))] : undefined,
        "Air Atk CI": isForecast ? [Math.round(d.airAttack * (1 - ciMargin)), Math.round(d.airAttack * (1 + ciMargin))] : undefined,
      };
    });

    return { chartData: mapped, forecasts: forecastData };
  }, [data, isPremium]);

  if (!data) {
    return (
      <p className="text-xs text-muted-foreground text-center py-8">
        No historical data available for this country.
      </p>
    );
  }

  // Calculate trend percentages
  const first = data[0];
  const last = data[data.length - 1];
  const trends = [
    { label: "Ballistic", change: ((last.ballistic - first.ballistic) / (first.ballistic || 1)) * 100 },
    { label: "Drones", change: ((last.drones - first.drones) / (first.drones || 1)) * 100 },
    { label: "Air Def", change: ((last.airDefense - first.airDefense) / (first.airDefense || 1)) * 100 },
    { label: "Air Attack", change: ((last.airAttack - first.airAttack) / (first.airAttack || 1)) * 100 },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-[10px] border-dashed border-amber-400/40 text-amber-400">
          <TrendingUp className="h-3 w-3 mr-1" />
          AI Forecast 2026–2028
        </Badge>
        <span className="text-[10px] text-muted-foreground">Dashed lines = projection · Shaded bands = confidence interval</span>
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis dataKey="year" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
            <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} width={50} />
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: "hsl(var(--foreground))" }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />

            {/* Historical solid areas */}
            <Area type="monotone" dataKey="Ballistic Missiles" stroke={COLORS.ballistic} fill={COLORS.ballistic} fillOpacity={0.25} connectNulls={false} />
            <Area type="monotone" dataKey="Drones & UAVs" stroke={COLORS.drones} fill={COLORS.drones} fillOpacity={0.25} connectNulls={false} />
            <Area type="monotone" dataKey="Air Defense" stroke={COLORS.airDefense} fill={COLORS.airDefense} fillOpacity={0.25} connectNulls={false} />
            <Area type="monotone" dataKey="Air Attack" stroke={COLORS.airAttack} fill={COLORS.airAttack} fillOpacity={0.25} connectNulls={false} />

            {/* Confidence interval bands */}
            <Area type="monotone" dataKey="Ballistic CI" stroke="none" fill={COLORS.ballistic} fillOpacity={0.1} connectNulls legendType="none" />
            <Area type="monotone" dataKey="Drones CI" stroke="none" fill={COLORS.drones} fillOpacity={0.1} connectNulls legendType="none" />
            <Area type="monotone" dataKey="Air Def CI" stroke="none" fill={COLORS.airDefense} fillOpacity={0.1} connectNulls legendType="none" />
            <Area type="monotone" dataKey="Air Atk CI" stroke="none" fill={COLORS.airAttack} fillOpacity={0.1} connectNulls legendType="none" />

            {/* Forecast dashed lines */}
            <Line type="monotone" dataKey="Ballistic (forecast)" stroke={COLORS.ballistic} strokeDasharray="6 3" strokeWidth={2} dot={{ r: 3, strokeDasharray: "" }} connectNulls />
            <Line type="monotone" dataKey="Drones (forecast)" stroke={COLORS.drones} strokeDasharray="6 3" strokeWidth={2} dot={{ r: 3, strokeDasharray: "" }} connectNulls />
            <Line type="monotone" dataKey="Air Def (forecast)" stroke={COLORS.airDefense} strokeDasharray="6 3" strokeWidth={2} dot={{ r: 3, strokeDasharray: "" }} connectNulls />
            <Line type="monotone" dataKey="Air Atk (forecast)" stroke={COLORS.airAttack} strokeDasharray="6 3" strokeWidth={2} dot={{ r: 3, strokeDasharray: "" }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Forecast summary */}
      {forecasts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: "Ballistic 2028", value: forecasts[2].ballistic, color: "text-red-400" },
            { label: "Drones 2028", value: forecasts[2].drones, color: "text-orange-400" },
            { label: "Air Def 2028", value: forecasts[2].airDefense, color: "text-blue-400" },
            { label: "Air Atk 2028", value: forecasts[2].airAttack, color: "text-purple-400" },
          ].map(f => (
            <div key={f.label} className="rounded-md border border-dashed border-muted-foreground/20 p-2 text-center">
              <p className="text-[10px] text-muted-foreground">{f.label}</p>
              <p className={`text-sm font-bold ${f.color}`}>{f.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Trend badges */}
      <div className="flex flex-wrap gap-2">
        {trends.map((t) => (
          <Badge
            key={t.label}
            variant="outline"
            className={`text-[10px] ${t.change > 0 ? "border-green-400/30 text-green-400" : t.change < 0 ? "border-red-400/30 text-red-400" : "border-yellow-400/30 text-yellow-400"}`}
          >
            {t.label}: {t.change > 0 ? "+" : ""}{t.change.toFixed(0)}% (2018–2025)
          </Badge>
        ))}
      </div>

      {!isPremium && (
        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
          <Lock className="h-3 w-3" /> Showing limited range — Upgrade to Premium for full 2018–2028 timeline with AI forecasts
        </p>
      )}
    </div>
  );
};

export default ArmsHistoryChart;
